import { useCallback, useEffect, useRef, useState } from "react";

import {
  GeneratedWorkflowDto,
  WorkflowJobName,
  WorkflowEventName,
  WorkflowProgressDto,
  WorkflowResultDto,
  WorkflowResultDtoData,
  WorkflowResultDtoWorkflowNameEnum,
  WorkflowEventResultSchemaMap,
} from "../api-client";
import useStore from "../helpers/useStore";
import notificationStore from "../stores/Notification/notificationStore";
import { WebSocketContext } from "../stores/WebSocket/WebSocket.provider";

/**
 * Discriminated union that maps each WorkflowEventName to its concrete DTO,
 * with `eventName` narrowed to the specific literal so TypeScript can
 * discriminate inside `if (evt.eventName === ...)` blocks.
 */
export type WorkflowEventDiscriminated = {
  [K in WorkflowEventName]: NonNullable<WorkflowEventResultSchemaMap[K]> & { eventName: K };
}[WorkflowEventName];

export type WorfklowResult = Extract<
  WorkflowResultDtoData,
  { workflowName: WorkflowResultDtoWorkflowNameEnum }
>;
export interface UseWorkflowOptions<T extends WorkflowResultDtoWorkflowNameEnum> {
  /**
   * The workflow name to filter for in the result handler
   */
  workflowName: T;

  /**
   * Callback invoked when workflow completes successfully with data
   */
  onResult?: (
    data: Omit<Extract<WorkflowResultDtoData, { workflowName: T }>, "workflowName">,
  ) => void;

  /**
   * Callback invoked when workflow emits workflow events successfully with data.
   * The union is discriminated on `eventName` so checking
   * `data.eventName === WorkflowEventName.X` narrows `data.data` automatically.
   */
  onWorkflowEvent?: (data: WorkflowEventDiscriminated) => void;

  /**
   * Callback invoked when workflow encounters errors
   */
  onError?: (errors: string) => void;

  /**
   * Callback invoked on each progress update
   */
  onProgress?: (progress: WorkflowProgressDto) => void;

  /**
   * Whether to show error notifications automatically
   * @default true
   */
  showErrorNotifications?: boolean;

  /**
   * Artificial progress increment speed (per 800ms interval)
   * @default 0.7
   */
  artificialProgressIncrement?: number;

  /**
   * Maximum artificial progress (prevents reaching 100% before actual completion)
   * @default 99
   */
  maxArtificialProgress?: number;

  /**
   * Buffer zone around actual progress for artificial incrementation
   * @default 10
   */
  progressBuffer?: number;
}

export interface UseWorkflowReturn {
  /**
   * Current loading state
   */
  workflowLoading: boolean;

  /**
   * Display progress (0-100) - includes artificial incrementation
   */
  displayProgress: number;

  /**
   * Actual job progress from backend
   */
  workflowProgress: WorkflowProgressDto | undefined;

  /**
   * Start/subscribe to a workflow job
   */
  subscribeToWorkflow: (job: GeneratedWorkflowDto) => void;

  /**
   * Manually set workflowLoading state
   */
  setWorkflowLoading: (workflowLoading: boolean) => void;

  /**
   * Reset all workflow state
   */
  reset: () => void;
}

/**
 * Custom hook for managing workflow execution, progress tracking, and result handling
 *
 * @example
 * ```tsx
 * const { workflowLoading, displayProgress, subscribeToWorkflow } = useWorkflow({
 *   workflowName: WorkflowFinalDataDtoWorkflowNameEnum.AppointmentSuggestionWorkflow,
 *   onResult: (data) => {
 *     setSolution(data);
 *   },
 *   onError: (errorMessage) => {
 *     console.error(errorMessage);
 *   }
 * });
 *
 * // When scheduling a job
 * const job = await scheduleAppointmentSuggestionJob(...);
 * if (job) {
 *   subscribeToWorkflow(job);
 * }
 * ```
 */
export const useWorkflow = <T extends WorkflowResultDtoWorkflowNameEnum>(
  options: UseWorkflowOptions<T>,
): UseWorkflowReturn => {
  const {
    workflowName,
    onResult,
    onWorkflowEvent,
    onError,
    onProgress,
    showErrorNotifications = true,
    artificialProgressIncrement = 0.7,
    maxArtificialProgress = 99,
    progressBuffer = 10,
  } = options;

  const WebSocketStore = useStore(WebSocketContext);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowProgress, setWorkflowProgress] = useState<WorkflowProgressDto | undefined>();
  const [displayProgress, setDisplayProgress] = useState<number>(0);
  const currentWorkflowId = useRef<string | undefined>();

  // Keep latest callbacks in refs so WebSocket handlers are stable and never stale
  const onResultRef = useRef(onResult);
  const onWorkflowEventRef = useRef(onWorkflowEvent);
  const onErrorRef = useRef(onError);
  const onProgressRef = useRef(onProgress);
  useEffect(() => {
    onResultRef.current = onResult;
    onWorkflowEventRef.current = onWorkflowEvent;
    onErrorRef.current = onError;
    onProgressRef.current = onProgress;
  });

  useEffect(() => {
    reset();
  }, []);
  // Use ref to track if handlers have been registered to prevent duplicate registrations
  const handlersRegistered = useRef(false);

  // Progress update handler — stable, reads latest callback from ref
  const workflowProgressHandler = useCallback((progressDto: WorkflowProgressDto) => {
    // Only handle progress for the subscribed workflow
    if (progressDto.workflowId !== currentWorkflowId.current) {
      return;
    }
    setWorkflowProgress(progressDto);
    // Only ever move the bar forward. The artificial ticker may have pushed
    // displayProgress ahead of the real percent; snapping it back down to the
    // (lower) actual value would make the bar visibly jump backwards.
    setDisplayProgress((prev) => Math.max(prev, progressDto.percent ?? 0));
    onProgressRef.current?.(progressDto);
  }, []);

  // Result handler — stable, reads latest callback from ref
  const workflowResultHandler = useCallback(
    (workflowData: WorkflowResultDto) => {
      // console.log("Workflow Data", workflowData);
      if (!workflowData) {
        setWorkflowLoading(false);
        return;
      }
      // Only handle results for the specified workflow
      if (workflowData && workflowData?.workflowId !== currentWorkflowId.current) {
        return;
      }

      // Handle successful result
      if (workflowData.data) {
        onResultRef.current?.(
          workflowData.data as Extract<WorkflowResultDtoData, { workflowName: T }>,
        );
      }

      // Handle errors
      if (workflowData.errors && workflowData.errors.length > 0) {
        const errorMessage = workflowData.errors.reduce(
          (error, currentErrorObj) => (error += currentErrorObj.error),
          "",
        );

        if (showErrorNotifications) {
          notificationStore.showMessage(errorMessage, "error");
        }

        onErrorRef.current?.(errorMessage);
      }

      // Unsubscribe from workflow tracking to prevent stale subscriptions on reconnect
      if (currentWorkflowId.current) {
        WebSocketStore.unsubscribe(
          "unsubscribeFromJob",
          `workflow:${currentWorkflowId.current}`,
          { workflowId: currentWorkflowId.current, message: "" },
        );
        currentWorkflowId.current = undefined;
      }

      setWorkflowLoading(false);
    },
    [showErrorNotifications, WebSocketStore],
  );

  // Event handler — stable, reads latest callback from ref
  const workflowEventHandler = useCallback(
    (eventDto: WorkflowEventResultSchemaMap[WorkflowEventName]) => {
      if (!eventDto) return;
      // Only handle results for the specified workflow
      if (eventDto.workflowId !== currentWorkflowId.current) {
        return;
      }

      onWorkflowEventRef.current?.(eventDto as WorkflowEventDiscriminated);
    },
    [],
  );

  // Register WebSocket event listeners
  useEffect(() => {
    // Prevent duplicate registration
    if (handlersRegistered.current) {
      return;
    }

    WebSocketStore.on("workflowProgress", workflowProgressHandler);
    WebSocketStore.on("workflowResult", workflowResultHandler);
    WebSocketStore.on("workflowEventResult", workflowEventHandler);
    handlersRegistered.current = true;

    // Cleanup function - removes listeners when component unmounts
    return () => {
      // Unsubscribe from workflow tracking to prevent stale subscriptions on reconnect
      if (currentWorkflowId.current) {
        WebSocketStore.unsubscribe(
          "unsubscribeFromJob",
          `workflow:${currentWorkflowId.current}`,
          { workflowId: currentWorkflowId.current, message: "" },
        );
      }
      WebSocketStore.off("workflowProgress", workflowProgressHandler);
      WebSocketStore.off("workflowResult", workflowResultHandler);
      WebSocketStore.off("workflowEventResult", workflowEventHandler);
      handlersRegistered.current = false;
    };
  }, [WebSocketStore, workflowProgressHandler, workflowResultHandler, workflowEventHandler]);

  // Artificial progress incrementation
  useEffect(() => {
    if (!workflowLoading) {
      return;
    }

    const id = setInterval(() => {
      setDisplayProgress((prev) => {
        const actual = workflowProgress?.percent ?? 0;

        // If actual progress has caught up, use it
        if (prev < actual) {
          return actual;
        }

        // Otherwise, increment artificially within constraints
        if (prev < actual + progressBuffer && prev < maxArtificialProgress) {
          return prev + artificialProgressIncrement;
        }

        return prev;
      });
    }, 800);

    return () => clearInterval(id);
  }, [
    workflowLoading,
    workflowProgress?.percent,
    artificialProgressIncrement,
    maxArtificialProgress,
    progressBuffer,
  ]);

  // Subscribe to a workflow job
  const subscribeToWorkflow = useCallback(
    (workflow: GeneratedWorkflowDto) => {
      currentWorkflowId.current = workflow.workflowId;
      setWorkflowLoading(true);
      setDisplayProgress(artificialProgressIncrement);
      setWorkflowProgress(undefined);
      WebSocketStore.subscribe("subscribeToWorkflow", `workflow:${workflow.workflowId}`, workflow);
    },
    [WebSocketStore],
  );

  // Reset all state
  const reset = useCallback(() => {
    setWorkflowLoading(false);
    setDisplayProgress(0);
    setWorkflowProgress(undefined);
  }, []);

  return {
    workflowLoading,
    displayProgress: Math.round(displayProgress),
    workflowProgress,
    subscribeToWorkflow,
    setWorkflowLoading,
    reset,
  };
};
